import React, { ReactNode } from "react"
import { IntlShape } from "react-intl"
import { Link as ReactRouterLink } from "react-router-dom"
import { Link } from "@carrotfertility/carotene-core"
import { RequestedProviderType } from "#/pages/provider-finder/types/ProviderRequest"
import { ProviderRequested } from "#/pages/provider-finder"
import { ProviderRecommendationConfig } from "../../hooks/useShowProviderRecommendations"
import { DbgPaths } from "#/utils/Paths"

export type ProviderVariantContent = {
  pageTitle: string
  pageHeading: string
  pageDescription: ReactNode
  additionalDetailsLabel: string
  additionalDetailsPlaceholder: string
  configFlag: keyof ProviderRecommendationConfig
}

export function getProviderVariantContent(
  intl: IntlShape,
  hasObGynFertilityCoverage = false
): Record<RequestedProviderType, ProviderVariantContent> {
  return {
    [ProviderRequested.Pediatrician]: {
      pageTitle: intl.formatMessage({ defaultMessage: "Find a trusted pediatrician" }),
      pageHeading: intl.formatMessage({ defaultMessage: "Find a trusted pediatrician" }),
      pageDescription: intl.formatMessage({
        defaultMessage:
          "Please provide some additional information to receive personalized pediatrician recommendations within 24-48 hours."
      }),
      additionalDetailsLabel: intl.formatMessage({ defaultMessage: "Additional preferences for a pediatrician" }),
      additionalDetailsPlaceholder: intl.formatMessage({
        defaultMessage: "Specialized in genetic disorders, developmental delays, etc."
      }),
      configFlag: "showPediatricianRecommendationsForm"
    },
    [ProviderRequested.OBGYN]: {
      pageTitle: intl.formatMessage({ defaultMessage: "Find a trusted OB/GYN" }),
      pageHeading: intl.formatMessage({ defaultMessage: "Find a trusted OB/GYN for pregnancy care" }),
      // if memember has OB/GYN fertility coverage, show link to fertility benefit guide
      pageDescription: hasObGynFertilityCoverage
        ? intl.formatMessage(
            {
              defaultMessage:
                "Please provide more information to receive personalized OB/GYN recommendations for an obstetric care provider. Our recommended providers receive high scores for low rates of unnecessary C-sections and excellent prenatal care. For information on OB/GYN eligibility for fertility services, <Link>visit your benefit guide</Link>."
            },
            {
              Link: (chunks) => (
                <Link component={ReactRouterLink} to={DbgPaths.FERTILITY_COVERAGE} color="inherit">
                  {chunks}
                </Link>
              )
            }
          )
        : intl.formatMessage({
            defaultMessage:
              "Please provide more information to receive personalized OB/GYN recommendations for an obstetric care provider. Our recommended providers receive high scores for low rates of unnecessary C-sections and excellent prenatal care."
          }),
      additionalDetailsLabel: intl.formatMessage({ defaultMessage: "Additional preferences for an OB/GYN" }),
      additionalDetailsPlaceholder: intl.formatMessage({
        defaultMessage: "Specialized in high risk pregnancies, delivering twins, etc."
      }),
      configFlag: "showObGynRecommendationsForm"
    }
  }
}

export function buildLanguageOptions(intl: IntlShape): { label: string; value: string }[] {
  // labels generated using node:
  // [ "ar", "zh", "fil", "fr", "de", "hi", "ko", "ru", "es", "vi"]
  //   .map((locale) => new Intl.DisplayNames([locale], { type: "language" }).of(locale))
  const optionsWithoutEnglishAndNoPreference = [
    { label: "العربية", value: "Arabic" },
    { label: "中文", value: "Chinese" },
    { label: "Filipino", value: "Filipino" },
    { label: "français", value: "French" },
    { label: "Deutsch", value: "German" },
    { label: "हिन्दी", value: "Hindi" },
    { label: "한국어", value: "Korean" },
    { label: "русский", value: "Russian" },
    { label: "español", value: "Spanish" },
    { label: "Tiếng Việt", value: "Vietnamese" }
  ]
  const sortedOptions = optionsWithoutEnglishAndNoPreference.sort((a, b) => a.label.localeCompare(b.label))

  return [
    { label: "English", value: "English" },
    ...sortedOptions,
    { label: intl.formatMessage({ defaultMessage: "No preference" }), value: "No preference" }
  ]
}
